import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type ArticleDraft = {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  cover_url: string | null;
  cover_alt: string | null;
  keyword: string | null;
  reason: string | null;
  word_count: number;
  section: string;
  status: "draft" | "published" | "rejected";
  published_at: string | null;
  created_at: string;
  updated_at: string;
};

/** Javni seznam objavljenih samodejnih člankov (za seznam, sitemap in podrobno stran). */
export const listPublishedDrafts = createServerFn({ method: "GET" }).handler(async () => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data, error } = await supabaseAdmin
    .from("article_draft")
    .select("slug, title, excerpt, content, cover_url, cover_alt, section, published_at")
    .eq("status", "published")
    .order("published_at", { ascending: false })
    .limit(500);
  if (error) return [];
  return data ?? [];
});

export const listArticleDrafts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<ArticleDraft[]> => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("article_draft")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(100);
    if (error) throw new Error(error.message);
    return (data ?? []) as ArticleDraft[];
  });

export const runArticleAutowriteNow = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { runArticleAutowrite } = await import("./article-autowrite.server");
    return await runArticleAutowrite();
  });

export const setArticleDraftStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { id: string; status: "draft" | "published" | "rejected" }) => {
    if (!data?.id || !["draft", "published", "rejected"].includes(data.status)) {
      throw new Error("Neveljavni podatki.");
    }
    return data;
  })
  .handler(async ({ context, data }) => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("article_draft")
      .update({
        status: data.status,
        published_at: data.status === "published" ? new Date().toISOString() : null,
        updated_at: new Date().toISOString(),
      })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const updateArticleDraft = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { id: string; title: string; excerpt: string; content: string }) => {
    if (!data?.id || !data.title?.trim()) throw new Error("Manjka naslov.");
    return {
      id: data.id,
      title: data.title.trim().slice(0, 200),
      excerpt: String(data.excerpt ?? "").slice(0, 400),
      content: String(data.content ?? ""),
    };
  })
  .handler(async ({ context, data }) => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const words = data.content.replace(/<[^>]+>/g, " ").split(/\s+/).filter(Boolean).length;
    const { error } = await supabaseAdmin
      .from("article_draft")
      .update({
        title: data.title,
        excerpt: data.excerpt,
        content: data.content,
        word_count: words,
        updated_at: new Date().toISOString(),
      })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const getAutoPublishSetting = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await supabaseAdmin
      .from("app_settings")
      .select("value")
      .eq("key", "auto_article_publish")
      .maybeSingle();
    return { enabled: String((data as { value?: string } | null)?.value ?? "").toLowerCase() === "true" };
  });

export const setAutoPublishSetting = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { enabled: boolean }) => ({ enabled: Boolean(data?.enabled) }))
  .handler(async ({ context, data }) => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("app_settings")
      .upsert({ key: "auto_article_publish", value: data.enabled ? "true" : "false" });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/** Javno branje enega objavljenega samodejnega članka po poti. */
export const getPublishedDraft = createServerFn({ method: "GET" })
  .inputValidator((data: { slug: string }) => ({ slug: String(data?.slug ?? "").slice(0, 120) }))
  .handler(async ({ data }) => {
    if (!data.slug) return null;
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row } = await supabaseAdmin
      .from("article_draft")
      .select("slug, title, excerpt, content, cover_url, cover_alt, section, published_at")
      .eq("slug", data.slug)
      .eq("status", "published")
      .maybeSingle();
    return row ?? null;
  });
